import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { vehicleListingStore } from '@/models/stores/vehicle-listings';
import type { VehicleHistory, VehicleListing } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { CircleSlash, SquareStack } from 'lucide-react';
import { useMemo, useRef } from 'react';
import {
  ComboboxPopover,
  type Option,
} from '../../ui/combobox/combobox-popover';
import { VehicleHistoryBadge } from './vehicle-history-badge';

export const getVehicleHistoryOptions = ({
  className,
}: {
  className: string;
}): Option<VehicleHistory>[] => {
  return [
    {
      value: 'No Accidents',
      label: 'No Accidents',
      icon: <CircleSlash className={className} />,
      hotkey: '1',
    },
    {
      value: '1+ Accidents',
      label: '1+ Accidents',
      icon: <SquareStack className={className} />,
      hotkey: '2',
    },
  ];
};

export const VEHICLE_HISTORY_OPTIONS_MAP = getVehicleHistoryOptions({
  className: 'h-3 w-3',
});

export type VehicleHistorySelectorProps = {
  id: string;
  value: VehicleListing['vehicle_history'] | null;
  hideCopy?: boolean;
  side?: 'left' | 'right' | 'bottom' | 'top';
  withHotkeyListener?: boolean;
  buttonVariant?: 'outline' | 'ghost';
  className?: string;
};

export function VehicleHistorySelector({
  id,
  value,
  hideCopy = false,
  side = 'left',
  withHotkeyListener = false,
  buttonVariant = 'ghost',
  className,
}: VehicleHistorySelectorProps) {
  const vehicleHistoryButtonRef = useRef<HTMLButtonElement>(null);

  const updateVehicleHistoryMutation = useMutation({
    onMutate: async ({ vehicleHistory }) => {
      vehicleListingStore.updateVehicleHistory({ id, vehicleHistory });
    },
    mutationFn: async ({
      vehicleHistory,
    }: {
      vehicleHistory: VehicleListing['vehicle_history'];
    }) => {
      return await pdkAxios.post(`/inventory/listings/${id}/vehicle_history`, {
        vehicle_history: vehicleHistory,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error updating vehicle history',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleVehicleHistoryChange = (
    newVehicleHistory: VehicleListing['vehicle_history'],
  ) => {
    updateVehicleHistoryMutation.mutate({ vehicleHistory: newVehicleHistory });
  };

  const currentOption = useMemo(() => {
    return VEHICLE_HISTORY_OPTIONS_MAP.find((option) => option.value === value);
  }, [value]);

  return (
    <div>
      <ComboboxPopover
        endAdornment={
          <KeyboardKeyIcon>
            {HOTKEYS.VEHICLE_LISTING_VEHICLE_HISTORY}
          </KeyboardKeyIcon>
        }
        placeholder="Set number of keys..."
        currentOption={currentOption}
        onChange={handleVehicleHistoryChange}
        emptyMessage="No vehicle history found"
        options={VEHICLE_HISTORY_OPTIONS_MAP}
        side={side}
        align="start"
        onKeyDown={(e) => {
          const keyPressed = e.key;

          const option = VEHICLE_HISTORY_OPTIONS_MAP.find(
            (option) => option.hotkey === keyPressed,
          );

          if (option) {
            handleVehicleHistoryChange(option.value);
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div>
          <VehicleHistoryBadge
            value={value ?? 'No Accidents'}
            hideCopy={hideCopy}
            buttonRef={vehicleHistoryButtonRef}
            buttonVariant={buttonVariant}
            className={cn('pl-0', className)}
          />
        </div>
      </ComboboxPopover>
      {withHotkeyListener && (
        <HotKeyListener
          hotkeys={HOTKEYS.VEHICLE_LISTING_VEHICLE_HISTORY}
          onHotkeyPressed={() => {
            vehicleHistoryButtonRef.current?.click();
          }}
        />
      )}
    </div>
  );
}
